import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, User, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Inquiry Submitted Successfully!",
      description: "We'll respond to your inquiry within 24 hours.",
    });
    
    // Reset form
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Call Us',
      content: companyInfo.phone,
      action: 'Call Now',
      href: `tel:${companyInfo.phone}`,
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: companyInfo.email,
      action: 'Send Email',
      href: `mailto:${companyInfo.email}`,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp',
      content: 'Quick Quote',
      action: 'Chat Now',
      href: companyInfo.whatsapp,
      color: 'text-green-600 bg-green-100'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Ready to discuss your material requirements? Our technical experts are here to help 
            you find the perfect solution for your industrial needs.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Contact Methods */}
          <div className="space-y-4 lg:space-y-6">
            <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6">
              Contact Information
            </h3>
            
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${method.color}`}>
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{method.title}</h4>
                        <p className="text-slate-600">{method.content}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(method.href, '_blank')}
                      >
                        {method.action}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Office Address */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-orange-600" />
                  <span>Visit Our Office</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-slate-700">
                  <div>{companyInfo.address.line1}</div>
                  <div>{companyInfo.address.line2}</div>
                  <div>{companyInfo.address.line3}</div>
                  <div className="font-medium">{companyInfo.address.city}</div>
                </div>
                
                <div className="flex items-center space-x-2 text-slate-600 mt-4">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Mon - Sat: 9:00 AM - 6:00 PM</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl lg:text-2xl text-slate-900">
                  Send Us Your Inquiry
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="border-slate-300 focus:border-orange-500"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-slate-700">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:border-orange-500"
                        placeholder="Enter your company name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="border-slate-300 focus:border-orange-500"
                        placeholder="Enter your email address"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:border-orange-500"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-slate-700">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="border-slate-300 focus:border-orange-500"
                      placeholder="Enter the subject of your inquiry"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="border-slate-300 focus:border-orange-500"
                      placeholder="Please provide details about your material requirements, quantities, delivery timeline, etc."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#022d47] hover:bg-[#033858] text-white py-3 text-lg"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Response Promise */}
        <div className="mt-16 text-center bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            Quick Response Guarantee
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-[#022d47] mb-2">&lt; 2 Hours</div>
              <div className="text-slate-600">Initial Response Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#022d47] mb-2">24/7</div>
              <div className="text-slate-600">Technical Support</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#022d47] mb-2">Same Day</div>
              <div className="text-slate-600">Quote Delivery</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};